import { apiClient } from "@/lib/api";
import type { LoginResponse } from "@/types/auth";
import type {
  SsoConnection,
  SsoConnectionWriteData,
  SsoResolveResponse,
} from "@/types/sso";

/**
 * SSO / SAML / OIDC federation API (Sprint 152, TASK-4333).
 *
 * Admin CRUD lives at `/api/v1/admin/sso/` (DRF ModelViewSet). Permission:
 * superadmin (all connections, both scopes) OR company_admin (own tenant only,
 * cannot create a platform-scope connection). A wrong role → 403; an
 * out-of-scope connection → 404.
 *
 * The list endpoint returns the DRF page envelope (`{ count, results }`); the
 * helper below unwraps `.results` (mirroring `api/featureFlags.ts` /
 * `api/plans.ts`).
 *
 * WRITE-ONLY SECRETS: `oidc_client_secret` and `saml_sp_private_key` are never
 * returned by the API. The read model instead carries `has_oidc_client_secret`
 * / `has_saml_sp_private_key` booleans. On EDIT, the form omits a secret key
 * when its input is left blank (keeps the stored secret) and includes it only
 * when the user types a replacement — an empty string is never sent, so a stored
 * secret is never accidentally wiped. On CREATE the fields are sent plainly.
 */

/** DRF page envelope shared by paginated list endpoints. */
interface PageEnvelope<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * List SSO connections visible to the caller (superadmin: all; company_admin:
 * own tenant only). Unwraps the paginated `{ count, results }` envelope and
 * asks for a large page so the (small) connection set is never truncated.
 */
export async function listSsoConnections(): Promise<SsoConnection[]> {
  const response = await apiClient.get<PageEnvelope<SsoConnection>>(
    "/v1/admin/sso/",
    { params: { page_size: 100 } },
  );
  return response.data.results;
}

/**
 * Create a new SSO connection. On create, secret fields are sent as plain
 * values. A scope/tenant mismatch or an empty `email_domain` returns a 400 with
 * field errors.
 */
export async function createSsoConnection(
  data: SsoConnectionWriteData,
): Promise<SsoConnection> {
  const response = await apiClient.post<SsoConnection>("/v1/admin/sso/", data);
  return response.data;
}

/**
 * Partially update an SSO connection. Secret fields must be OMITTED from `data`
 * when the user did not enter a replacement — sending an empty string would wipe
 * the stored secret (the caller is responsible for dropping blank secret keys).
 */
export async function updateSsoConnection(
  id: number,
  data: SsoConnectionWriteData,
): Promise<SsoConnection> {
  const response = await apiClient.patch<SsoConnection>(
    `/v1/admin/sso/${id}/`,
    data,
  );
  return response.data;
}

/** Delete an SSO connection. Returns 204. */
export async function deleteSsoConnection(id: number): Promise<void> {
  await apiClient.delete(`/v1/admin/sso/${id}/`);
}

/**
 * Resolve an email to its SSO login URL (AllowAny). Returns
 * `{ sso_login_url }` on a 200 match; the caller navigates the full browser to
 * that server path (`window.location.assign`) to start SP-initiated login. A
 * 404 (no SSO connection for this email's domain) propagates as a rejected
 * promise for the caller to surface inline.
 */
export async function resolveSso(email: string): Promise<SsoResolveResponse> {
  const response = await apiClient.get<SsoResolveResponse>(
    "/v1/auth/sso/resolve/",
    { params: { email } },
  );
  return response.data;
}

/**
 * Exchange a single-use SSO callback `code` for platform tokens (AllowAny). The
 * response is the SAME union as password login: either a `LoginSuccess`
 * (`{ access, refresh, user }`) or an MFA challenge (`{ mfa_required, mfa_token }`)
 * when the mapped user has MFA enabled. The code is single-use and short-lived
 * (~120s); a missing/expired/used code → 400 `{ detail }`.
 */
export async function exchangeSsoCode(code: string): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>(
    "/v1/auth/sso/exchange/",
    { code },
  );
  return response.data;
}
